/* eslint-disable import/no-unresolved */
import { useMutation, useReactiveVar } from '@apollo/client';
import { Button, Table } from 'appkit-react';
import { useEffect, useState } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';

import { VALIDATE_REMEDIATION_FOR_FRY7Q } from 'Apollo/Mutations/Rules/Fry7qResults';
import { tenantIdVar } from 'Apollo/config';
import ImpactedRules from './ImpactedRules';

const Remediation = ({ failedTestsData, refetch, refetchTestData }) => {
    const location = useLocation();
    const [batchId, setBatchId] = useState(location.state.batchId);
    const [ruleId, setRuleId] = useState(location.state.ruleId);
    const { getFailedTestResultsForFRY7Q: { remediation } } = failedTestsData;
    const [enableValidate, setEnableValidate] = useState(false);
    const [showImpactModal, setShowImpactModal] = useState(false);
    const [updateRemediation, setUpdateRemediation] = useState([]);
    const tenantId = useReactiveVar(tenantIdVar);

    /* Apollo mutation for rule execution  */
    const [fry7qValidationRuleExecution, { data, loading, error }] = useMutation(VALIDATE_REMEDIATION_FOR_FRY7Q);

    /**  Renders editiable cell in table */
    const renderEditCell = cellInfo => {
        const { index, column: { id } } = cellInfo;
        return (
            <div
                contentEditable
                suppressContentEditableWarning
                className="true-comply-editted-cell"
                onBlur={e => {
                    const editedRemediation = [...updateRemediation];
                    editedRemediation[index][id] = e.target.innerHTML;
                    // eslint-disable-next-line dot-notation
                    editedRemediation[index]['modified'] = true;
                    setUpdateRemediation(editedRemediation);
                    setEnableValidate(true);
                }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: updateRemediation[index][id],
                }}
            />
        );
    };

    /** Calls the fry7qValidationRuleExecution and refetched the test data */
    const onValidate = async () => {
        const input = {
            batchId: Number(batchId),
            rowId: parseInt(failedTestsData.getFailedTestResultsForFRY7Q.criteria.rowId, 10),
            ruleId: Number(ruleId),
            tenantId: parseInt(tenantId, 10),
        };
        updateRemediation.filter(remedi => remedi.modified)
            .forEach(re => {
                const key = re.mdrm;
                // eslint-disable-next-line no-nested-ternary
                const newInputval = re.newValue ? Number(re.newValue) : null;
                input[key] = re.type === 'INTEGER' ? newInputval : re.newValue;
            });
        try {
            await fry7qValidationRuleExecution({
                variables: {
                    input,
                },
            });
            setShowImpactModal(!showImpactModal);
            refetch();
            refetchTestData();
        } catch (err) {
            //
        }
    };

    /** Column configuration */
    const columns = [{
        Header: 'MDRM',
        accessor: 'mdrm',
        minWidth: 50,
    }, {
        Header: 'Description',
        accessor: 'description',
        minWidth: 200,
    }, {
        Header: 'Original Value',
        accessor: 'value',
        minWidth: 50,
        Cell: props => {
            const { original: { type, value } } = props;
            const updateValue = type === 'INTEGER' ? Number(value).toFixed(0) : value;
            return <span>{updateValue}</span>;
        },
    }, {
        Header: 'New Value',
        accessor: 'newValue',
        minWidth: 50,
        Cell: renderEditCell,
    }];

    /* Side effect for modified remediation */
    useEffect(() => {
        const modifiedRemediation = remediation.map(remedi => ({
            ...remedi,
            newValue: remedi.type === 'INTEGER' ? Number(remedi.value).toFixed(0) : remedi.value,
        }));
        setUpdateRemediation(modifiedRemediation);
    }, []);

    /* Side effect when location state updates */
    useEffect(() => {
        setBatchId(location.state.batchId);
        setRuleId(location.state.ruleId);
    }, [location]);

    if (error) {
        const { message } = error;
        return <Alert variant="danger">{message}</Alert>;
    }

    if (data && showImpactModal) {
        return (
            <ImpactedRules
                data={data}
                setShowImpactModal={setShowImpactModal}
                updateRemediation={updateRemediation}
                rowId={failedTestsData.getFailedTestResultsForFRY7Q.criteria.rowId}
            />
        );
    }

    return (
        <div className="true-comply-result__remediation">
            {
                remediation.length && (
                    <Row>
                        <Col>
                            <div className="table-demo">
                                <Table
                                    className="true-comply-result__remediation-table"
                                    data={updateRemediation}
                                    columns={columns}
                                    showPagination={false}
                                    striped
                                    resizable
                                    noDataText="No Remediation ..!!!"
                                />
                            </div>
                        </Col>
                    </Row>
                )
            }
            <Row className="marginTop30">
                <Col>
                    <Button size="xl" className="float-right" onClick={onValidate} id="validateButton" disabled={!enableValidate} isLoading={loading}>Validate</Button>
                </Col>
            </Row>
        </div>
    );
};

export default Remediation;
